import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { dehydrate, QueryClient } from 'react-query';

import {
  CARD_API_PATH,
  listBanksaladCardPromotionGroups,
} from '@/page-src/chart/cards/pages/cashback/api';
import { getChartFilterFromQuery } from '@/page-src/chart/utils/get-chart-filter-from-query';
import { getComparableDeposits, getRankDeposits } from '@/page-src/product/pages/deposits/api';
import { ProductDepositsDetailMain } from '@/page-src/product/pages/deposits/components/main';
import { INSTALLMENT_SAVINGS_KVS_KEY } from '@/page-src/product/pages/installment-savings/pages/product-guid/constants';
import { getSavingsRequestFilters } from '@/src/@domain/savings/utils/get-request-filters';
import { KVS_SHOPPING_SERVICE_ID } from '@/src/@domain/shopping/constants';
import { getKeyValueStore } from '@/src/apis/kvs';
import { getMetaTag } from '@/src/apis/meta-tag';
import { API_PATH } from '@/src/constants/api-path';
import { URL_PATH } from '@/src/constants/url-path';
import { getIsWebViewFromRequest } from '@/src/utils/env';

export const getServerSideProps = async ({ req, query }: GetServerSidePropsContext) => {
  const { productGuid } = query;

  if (typeof productGuid !== 'string') {
    return {
      notFound: true,
    };
  }

  const pathname = req.url?.split('?')[0] ?? '';

  const queryClient = new QueryClient();
  const isWebView = getIsWebViewFromRequest(req);

  const primeRateConditionTypes = getChartFilterFromQuery<string[]>(
    query,
    'prime-rate-condition',
    []
  );

  const month =
    query['registration-months'] !== undefined ? Number(query['registration-months']) : undefined;

  const parsedPrimeRateConditions = getSavingsRequestFilters(primeRateConditionTypes, true);

  try {
    await Promise.all([
      queryClient.fetchQuery([API_PATH.GET_COMPARABLE_DEPOSIT(productGuid)], () =>
        getComparableDeposits(
          productGuid,
          {
            registrationMonths: month,
            savingsPrimeRateConditionTypeEnums: parsedPrimeRateConditions,
            productGuid,
            isAllPrimeRateEnabled: parsedPrimeRateConditions.length === 0,
          },
          req
        )
      ),
      queryClient.fetchQuery([API_PATH.GET_RANK_DEPOSIT(productGuid)], () =>
        getRankDeposits(productGuid, req)
      ),
      queryClient.prefetchQuery([KVS_SHOPPING_SERVICE_ID, INSTALLMENT_SAVINGS_KVS_KEY], () =>
        getKeyValueStore(KVS_SHOPPING_SERVICE_ID, INSTALLMENT_SAVINGS_KVS_KEY, req)
      ),
      queryClient.fetchQuery([CARD_API_PATH.LIST_BANKSALAD_CARD_PROMOTION_GROUP], () =>
        listBanksaladCardPromotionGroups(req)
      ),
      queryClient.fetchQuery([API_PATH.META_TAG], () =>
        getMetaTag(
          {
            path: pathname,
          },
          req
        )
      ),
    ]);

    return {
      props: {
        dehydratedState: dehydrate(queryClient),
        isWebViewWithSsr: isWebView,
        productGuid,
      },
    };
  } catch (error) {
    return {
      redirect: {
        destination: URL_PATH.CHART_DEPOSITS,
        permanent: false,
      },
    };
  }
};

const ProductDepositSavings = ({
  productGuid,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => (
  <ProductDepositsDetailMain productGuid={productGuid} />
);

export default ProductDepositSavings;
